import R from 'ramda';
import moment from 'moment';
import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Container,
  Form,
  Segment,
  Label,
  Grid,
  Image,
} from 'semantic-ui-react';

import {
  STATUS,
  MECHANISMS,
  SUBLICENSE_DISPLAY_NAMES,
  SUBLICENSE_OPTIONS_BY_MAX_VERSION,
  MAX_VERSIONS
} from '../../../server/constants'

import {
  formatDate,
  getValue,
  getFsUrl,
} from '_utils';

import { get } from "_utils/xhr";

import {
  ProductActivatedInfoText,
  ProductDeactivatedByText
} from './util';

import BaseDateField from '_forms/baseDate'
import LineItem from '_views/lineItem'
import useAdminUser from '../../hooks/useAdminUser';
import { isAuthAdmin, isLicenseAdmin } from '../../utils';
import useCanAdminLicenses from '../../hooks/useCanAdminLicenses';

const confirmDeactivate = (onRemove, payload) => _e => {
  if (window.confirm('Deactivate this Add On license?')) onRemove(payload);
};

const Sublicense = ({
  licenseId,
  sublicense,
  onRemoveSublicense
}) => {
  const canAdminLicense = useCanAdminLicenses();
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [idImageMimeType, setIdImageMimeType] = useState(null);

  const fsInfo = sublicense.activated_info.fastspring;
  const orderRefs = fsInfo && fsInfo.reference ? fsInfo.reference.split(" ") : [];

  useEffect(() => {
    const idImage = fsInfo && fsInfo.surveys && fsInfo.surveys.id_image;

    if (idImageUrl || !idImage) {
      return;
    }

    get(`/id_image/${idImage}`, {})
      .then((body) => {
        setIdImageUrl(body.sig);
        setIdImageMimeType(body["content-type"]);
      });
  }, [fsInfo]);

  return (
    <Segment tertiary={ R.propEq('status', STATUS.NOT_ACTIVE, sublicense) } >
      <Form as={ Container } size="tiny">
        <Form.Group>
          <Form.Input width={ 8 } label='Name' name='name' value={ sublicense.name } readOnly />
          <Form.Input width={ 8 } label='Type' name='type' value={ sublicense.type } readOnly />
        </Form.Group>
        <Form.Group>
          <Form.Input width={ 8 } label='Status' name='status' value={ sublicense.status !== STATUS.ACTIVE ? "Deactivated" : "Active" } readOnly />
          <Form.Input width={ 8 } label='Created On' name='activated_on' value={ formatDate(sublicense.activated_on) } readOnly />
        </Form.Group>
        <Form.Group>
          {sublicense.type === 'full' ? null : (
            <Form.Input width={8} label='Expires On' name='expires_on' value={formatDate(sublicense.expires_on)} readOnly />
          )}
          {sublicense.status !== 'active' ? (
            <Form.Input width={8} label='Deactivated On' name='deactivated_on' value={formatDate(sublicense.deactivated_on)} readOnly />
          ) : null}
        </Form.Group>

        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column width={8}>
              <LineItem label='Created By' text={ ProductActivatedInfoText(sublicense) } />
              <LineItem label='Deactivated By' text={ ProductDeactivatedByText(sublicense) } />
              <LineItem label='Id' text={ sublicense._id } />

              <Button.Group size="tiny">
                {
                  canAdminLicense && R.propEq('status', STATUS.ACTIVE, sublicense) && !R.pathEq(['activated_info', 'mechanism'], MECHANISMS.FASTSPRING, sublicense)
                    ? (
                      <Button
                        negative
                        content="Deactivate"
                        size="mini"
                        onClick={ confirmDeactivate(onRemoveSublicense, { licenseId, sublicenseId: sublicense._id }) }
                      />
                    ) : null
                }
              </Button.Group>
            </Grid.Column>

            <Grid.Column width={8}>
              {fsInfo ? (
                <Fragment>
                  <p><strong>Fastspring information:</strong></p>
                  <LineItem label={orderRefs.length === 1 ? "Order" : "Orders"}>
                    {orderRefs.map((ref) => (
                      <Label
                        key={ref}
                        as="a"
                        style={{ marginBottom: "0.25em" }}
                        href={getFsUrl("order")(ref)}
                        target="_blank"
                        content={ref}
                      />
                    ))}
                  </LineItem>
                  <LineItem label="Reference" text={fsInfo.reference_user} />
                  <LineItem label="Purchased on" text={fsInfo.created} isDate />
                  {fsInfo.customer ? (
                    <Fragment>
                      <LineItem label="Customer Name" text={fsInfo.customer.full_name} />
                      <LineItem label="Customer Email" text={fsInfo.customer.email} />
                    </Fragment>
                  ) : null}
                  <LineItem label="Status" text={fsInfo.completed && fsInfo.completed.status} />
                  {sublicense.type === "subscription" && fsInfo.subscription ? (
                    <Fragment>
                      <LineItem
                        label="Subscription Reference"
                        href={getFsUrl("sub")(fsInfo.subscription.reference)}
                        text={fsInfo.subscription.reference}
                      />
                      <LineItem label="Subscription Status" text={fsInfo.subscription.status} />
                      <LineItem label="Subscription Status Reason" text={fsInfo.subscription.status_reason} />
                      <LineItem label="Subscription Status Changed" text={fsInfo.subscription.status_changed_on} isDate />
                      <LineItem label="Subscription Next Period" text={fsInfo.subscription.next_period} isDate />
                      <LineItem label="Subscription Ends On" text={fsInfo.subscription.end_date} isDate />
                    </Fragment>
                  ) : null}
                  <LineItem label="Return Status" text={fsInfo.returned || "None"} />
                  {sublicense.deactivated_info && sublicense.deactivated_info.return ? (
                    <LineItem
                      label="Returned"
                      href={getFsUrl("return")(sublicense.deactivated_info.return.id)}
                      text={sublicense.deactivated_info.return.id}
                    />
                  ) : null}
                  {idImageUrl ? (
                    <Fragment>
                      <p style={{ marginTop: "16px" }}><strong>Academic ID:</strong></p>
                      {/pdf/i.test(idImageMimeType || "") ? (
                        <a href={idImageUrl} target="_blank" rel="noopener noreferrer">ID Image PDF</a>
                      ) : (
                        <Image src={idImageUrl} style={{ maxWidth: "200px", maxHeight: "200px" }} />
                      )}
                      <LineItem label="School Name" text={fsInfo.surveys.school_name} />
                      <LineItem label="Field of Study" text={fsInfo.surveys.field_of_study} />
                      <LineItem label="Student Purchase Reason" text={fsInfo.surveys.student_purchase_reason} />
                    </Fragment>
                  ) : null}
                </Fragment>
              ) : null}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </Segment>
  );
};

Sublicense.propTypes = {
  sublicense: PropTypes.object.isRequired,
  licenseId: PropTypes.string.isRequired,
  onRemoveSublicense: PropTypes.func.isRequired
}

const AddSublicense = ({
  licenseName,
  onSave,
  onCancel
}) => {
  const subLicenseOptions = SUBLICENSE_OPTIONS_BY_MAX_VERSION[licenseName].map(id => ({ text: SUBLICENSE_DISPLAY_NAMES[id], value: id }));

  const [, setDirty] = useState(false);
  const [values, setValues] = useState({
    name: subLicenseOptions.length ? subLicenseOptions[0].value : "",
    type: 'full',
    expires_on: moment().add(30, 'days'),
  });

  const addOnTypeOptions = [
    {text: 'Full', value: 'full'},
    {text: 'Term', value: 'term'}
  ];

  const onValueChange = (e, { name, value, type, checked }) => {
    // Set default expiry on type change
    let expiry = {};
    if (name === 'type') {
      expiry = { expires_on: moment().add(value === 'demo' ? 14 : 30, 'days') }
    }

    const newLic = Object.assign({}, values, { [name]: getValue({name, value, type, checked}) }, expiry);
    setValues(newLic);
    setDirty(true);
  };

  const saveLicense = () => onSave(values);

  return (
    <Segment>
      <Form as={ Container } >
        <Form.Group>
          <Form.Select width={ 6 } label='Product' options={ subLicenseOptions } name='name' value={ values.name } onChange={ onValueChange } />
          <Form.Select width={ 6 } label='Type' options={ addOnTypeOptions } name='type' value={ values.type } onChange={ onValueChange } />
          { values.type === 'full'
            ? null
            : <BaseDateField
              width={ 4 }
              name="expires_on"
              value={values.expires_on}
              onChange={ onValueChange }
              labelTxt='Expires'
            />
          }
        </Form.Group>
        <Button.Group>
          <Button onClick={ onCancel } >Cancel</Button>
          <Button.Or />
          <Button positive onClick={ saveLicense } >Save</Button>
        </Button.Group>
      </Form>
    </Segment>
  );
};

AddSublicense.propTypes = {
  licenseName: PropTypes.oneOf(Object.values(MAX_VERSIONS)).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const Sublicenses = ({
  licenseId,
  licenseName,
  sublicenses = [],
  onAddSublicense,
  onRemoveSublicense
}) => {

  const adminUser = useAdminUser();
  const canAdminLicense = isAuthAdmin(adminUser) || isLicenseAdmin(adminUser);

  const [showAddLicense, setAddLicense] = useState(false);
  const onSaveNewSublicense = payload => {
    onAddSublicense({ licenseId, payload });
    setAddLicense(false);
  };

  const activeSublicenses = sublicenses.filter((sl) => sl.status === STATUS.ACTIVE);
  const inactiveSublicenses = sublicenses.filter((sl) => sl.status !== STATUS.ACTIVE);

  const hasSubLicenseAddOptions = !SUBLICENSE_OPTIONS_BY_MAX_VERSION[licenseName].length;

  return (
    <Fragment>
      {
        canAdminLicense ? (
          <Button onClick={ () => setAddLicense(true) } disabled={ hasSubLicenseAddOptions || showAddLicense } size="small">
            { hasSubLicenseAddOptions ? `${licenseName} does not support any addons` : "Add" }
          </Button>
        ) : null
      }
      {
        !showAddLicense && (
          <Segment.Group>
            {
              activeSublicenses.map(sublic => (
                <Sublicense key={ sublic._id } sublicense={ sublic } licenseId={ licenseId } onRemoveSublicense={ onRemoveSublicense } />
              ))
            }
            {
              inactiveSublicenses.map(sublic => (
                <Sublicense key={ sublic._id } sublicense={ sublic } licenseId={ licenseId } onRemoveSublicense={ onRemoveSublicense } />
              ))
            }
          </Segment.Group>
        )
      }
      {
        showAddLicense && (
          <Segment.Group>
            <AddSublicense licenseId={ licenseId } licenseName={ licenseName } onCancel={ () => setAddLicense(false) } onSave={ onSaveNewSublicense } />
          </Segment.Group>
        )
      }
    </Fragment>
  );
};
Sublicenses.propTypes = {
  sublicenses: PropTypes.array.isRequired,
  licenseId: PropTypes.string.isRequired,
  licenseName: PropTypes.oneOf(Object.values(MAX_VERSIONS)).isRequired,
  onAddSublicense: PropTypes.func.isRequired,
  onRemoveSublicense: PropTypes.func.isRequired
}

export default Sublicenses



// WEBPACK FOOTER //
// ./webAdmin/views/license/sublicenses.js